import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown, Equal, LoaderCircle } from 'lucide-react';
import { useAuth } from 'oidc-react';
import { useParams, useLocation } from 'react-router-dom';
import Pagination from '../../components/Pagination/Pagination';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useAgentHeartBeatService } from '../../components/services/agentHeartBeatService';
import Project from '../../common/Utils/project';
import api from '../../components/services/apiService';
import PatchDetailsModal from '../../components/Modal/PatchDetailsModal';
import { CardTitle } from '../../components/Card';
import { AgentStatusIndicator } from '../../components/AgentStatus';
import { DataTable, ColumnConfig } from '../../components/Table';
import moment from 'moment';
import { cveTheme, getCveCvssColor, getCveSeverityStyle } from '../../common/themes/cveColors';

const itemsPerPage = 20;

// ================= CVE HELPERS =================
const SeverityBadge = ({ severity }: { severity: string }) => {
  const style = getCveSeverityStyle(severity);
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider border"
      style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
    >
      {severity}
    </span>
  );
};

const DeviceNeedingPatch = () => {
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { getAgentHeartbeatStatus } = useAgentHeartBeatService();
  const { id } = useParams();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const rowDetails = location.state;
  const kbid = queryParams.get('kbid');
  const kbTitle = queryParams.get('title');
  const kbCategory = queryParams.get('category');
  const patchType = queryParams.get('type');
  const packageIdentifier = queryParams.get('packageIdentifier');
  const packageName = queryParams.get('packageTitle');
  const version = queryParams.get('version');

  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<any>(null);
  const [heartbeatData, setHeartbeatData] = useState<any[]>([]);
  const [showPatchModal, setShowPatchModal] = useState(false);

  const [banner, setBanner] = useState({
    packageName: '',
    targetVersion: '',
    targetVersionDate: '',
    Category: '',
    kbid: '',
  });

  const getAgentHeartbeats = async (agentIds: string[]) => {
    try {
      const data = await getAgentHeartbeatStatus(selectedTenantId as string, agentIds);
      return data?.isSuccess && Array.isArray(data.data) ? data.data : [];
    } catch {
      return [];
    }
  };

  const getAgentsNeedingPatch = async (page = 1) => {
    try {
      setLoading(true);
      const query = `?pageNumber=${page}&pageSize=${itemsPerPage}`;

      let response;

      if (patchType === 'osUpdate') {
        response = await api.post(`${Project.api}patchreposervice/SoftwarePatch/${id}/os-update-available-agents/${kbid}${query}`);

        const data = (response?.data?.data || []).filter((a: any) => a.agentName);
        setAgents(data);

        setBanner({
          packageName: kbTitle || '',
          Category: kbCategory || '',
          kbid: kbid || '',
          targetVersion: '',
          targetVersionDate: '',
        });

        setPagination(response?.data?.pagination ?? null);
      } else if (patchType === 'osUpdateInstalled') {
        response = await api.post(`${Project.api}patchreposervice/SoftwarePatch/${id}/os-update-installed-agents/${kbid}${query}`);

        const data = (response?.data?.data || []).filter((a: any) => a.agentName);
        setAgents(data);

        setBanner({
          packageName: kbTitle || '',
          Category: kbCategory || '',
          kbid: kbid || '',
          targetVersion: '',
          targetVersionDate: '',
        });

        setPagination(response?.data?.pagination ?? null);
      } else {
        response = await api.post(`${Project.api}patchreposervice/SoftwarePatch/${id}/software-patches-available-agents${query}`, {
          PackageIdentifier: packageIdentifier,
          Version: version,
        });

        const data = (response?.data?.data || []).filter((a: any) => a.agentName);
        setAgents(data);

        if (data.length > 0) {
          setBanner({
            packageName: packageName ?? '',
            targetVersion: stripVersionPrefix(data[0]?.targetVersion),
            Category: data[0]?.category ?? '',
            targetVersionDate: data[0]?.targetVersionDate ?? '',
            kbid: '',
          });
        }

        setPagination(response?.data?.pagination ?? null);
      }

      const agentIds = response?.data?.data?.map((a: any) => a.agentId);
      const heartbeats = await getAgentHeartbeats(agentIds);
      setHeartbeatData(heartbeats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.userData) {
      getAgentsNeedingPatch(1);
    }
  }, [auth, patchType]);

  const handlePageChange = (page: number) => {
    getAgentsNeedingPatch(page);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <LoaderCircle className="animate-spin w-10 h-10 text-white" />
      </div>
    );
  }

  const renderOperationIcon = (current: string, target: string) => {
    if (!current || !target) return <Equal className="text-gray-400" />;
    const cmp = compareVersions(current, target);
    if (cmp === 0) return <Equal className="text-gray-400" />;
    return cmp < 0 ? <ArrowUp className="text-green-500" /> : <ArrowDown className="text-red-500" />;
  };

  const baseText = 'truncate w-full';

  const columns: ColumnConfig[] =
    patchType === 'osUpdate' || patchType === 'osUpdateInstalled'
      ? [
          {
            key: 'agentName',
            header: 'Device Name',
            width: '35%',
            render: (_, item) => <div className={baseText}>{item.agentName ?? '-'}</div>,
          },
          {
            key: 'groupName',
            header: 'Group',
            width: '35%',
            render: (_, item) => <div className={baseText}>{item.groupName ?? '-'}</div>,
          },
          {
            key: 'status',
            header: 'Status',
            width: '20%',
            align: 'center',
            render: (_, item) => {
              const hb = heartbeatData.find((h) => h.tenantAgentId === item.agentId);
              return (
                <div className="flex justify-center items-center h-full">
                  <AgentStatusIndicator
                    agentId={item.agentId}
                    isActive={hb?.agentStatus === 'Active'}
                    statusMessage={hb?.heartbeatStatusMessage}
                  />
                </div>
              );
            },
          },
        ]
      : [
          {
            key: 'agentName',
            header: 'Device Name',
            width: '15%',
            render: (_, item) => <div className={baseText}>{item.agentName ?? '-'}</div>,
          },
          {
            key: 'groupName',
            header: 'Group',
            width: '25%',
            render: (_, item) => <div className={baseText}>{item.groupName ?? '-'}</div>,
          },
          {
            key: 'currentVersion',
            header: 'Current Version',
            width: '15%',
            render: (_, item) => stripVersionPrefix(item.currentVersion) || '-',
          },
          {
            key: 'currentVersionDate',
            header: 'Current Version Date',
            width: '15%',
            render: (_, item) =>
              item?.currentVersionDate && !item.currentVersionDate.startsWith('0001-01-01')
                ? moment(item.currentVersionDate).format('DD MMM YYYY')
                : '-',
          },
          {
            key: 'operationType',
            header: 'Operation',
            width: '10%',
            align: 'center',
            render: (_, item) => (
              <div className="flex justify-center items-center">{renderOperationIcon(item.currentVersion, item.targetVersion)}</div>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            width: '10%',
            align: 'center',
            render: (_, item) => {
              const hb = heartbeatData.find((h) => h.tenantAgentId === item.agentId);
              return (
                <div className="flex justify-center items-center">
                  <AgentStatusIndicator
                    agentId={item.agentId}
                    isActive={hb?.agentStatus === 'Active'}
                    statusMessage={hb?.heartbeatStatusMessage}
                  />
                </div>
              );
            },
          },
        ];

  return (
    <div className="p-4 bg-black rounded text-white">
      {/* Banner */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-4">{banner.packageName}</h2>

        <div
          className="grid gap-4 w-full"
          style={{
            gridTemplateColumns: 'repeat(3, 1fr)',
          }}
        >
          {(patchType === 'osUpdate'
            ? [
                { label: 'KB ID', value: banner.kbid || 'N/A' },
                { label: 'Category', value: banner.Category || 'N/A' },
                { label: 'Total Devices', value: pagination?.totalRecords ?? 0 },
              ]
            : [
                { label: 'Target Version', value: banner.targetVersion },
                {
                  label: 'Target Version Date',
                  value:
                    banner.targetVersionDate && !banner.targetVersionDate.startsWith('0001-01-01')
                      ? moment(banner.targetVersionDate).format('DD MMM YYYY')
                      : 'N/A',
                },
                { label: 'Total Devices', value: pagination?.totalRecords ?? 0 },
              ]
          ).map((card, i) => (
            <div
              key={i}
              style={{ backgroundColor: i % 2 === 0 ? '#2E2D2D' : '#1D1D1D' }}
              className="h-[120px] p-4 rounded-lg shadow flex flex-col overflow-hidden"
            >
              <div className="mb-2">
                <CardTitle title={card.label} />
              </div>
              <div className="flex-1 flex items-center">
                <span className="text-3xl font-bold ml-[28px] truncate w-[90%]">{card.value}</span>
              </div>
            </div>
          ))}
        </div>

        {/* CVE DETAILS — shown when navigated from VulnerabilityCenter with row state */}
        {rowDetails && (
          <div
            className="mt-5 mb-2 flex items-center justify-between w-full px-4 py-3 rounded-lg"
            style={{ backgroundColor: cveTheme.panelBackground, border: `1px solid ${cveTheme.panelBorder}` }}
          >
            {/* CVE ID link */}
            <a
              href={rowDetails.cveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 font-mono font-semibold text-sm transition-colors"
              style={{ color: cveTheme.link }}
            >
              {rowDetails.cveId}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3 h-3"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>

            <span className="w-px h-5 bg-[#333]" />

            {/* Severity badge */}
            <SeverityBadge severity={rowDetails.severity} />

            <span className="w-px h-5 bg-[#333]" />

            {/* CVSS */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: cveTheme.label }}>
                CVSS
              </span>
              <span className="text-sm font-bold tabular-nums" style={{ color: getCveCvssColor(rowDetails.cvssScore) }}>
                {rowDetails.cvssScore?.toFixed(1)}
              </span>
              <span className="text-[11px]" style={{ color: cveTheme.labelMuted }}>
                / 10
              </span>
            </div>

            <span className="w-px h-5 bg-[#333]" />

            {/* EPSS */}
            {rowDetails.epssScore != null && (
              <>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: cveTheme.label }}>
                    EPSS
                  </span>
                  <span className="text-sm font-bold tabular-nums" style={{ color: cveTheme.epss }}>
                    {(rowDetails.epssScore * 100).toFixed(2)}%
                  </span>
                </div>
                <span className="w-px h-5 bg-[#333]" />
              </>
            )}

            {/* KEV */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: cveTheme.label }}>
                KEV
              </span>
              {rowDetails.kev ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: cveTheme.kevListed }}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: cveTheme.kevDot }} />
                  Listed
                </span>
              ) : (
                <span className="text-xs font-medium" style={{ color: cveTheme.kevNotListed }}>
                  Not listed
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <DataTable columns={columns} data={agents} loading={loading} hoverEffect={true} />

      <Pagination
        currentPage={pagination?.pageNumber}
        totalPages={pagination?.totalPages ?? 1}
        loading={loading}
        onPageChange={handlePageChange}
      />

      {showPatchModal && (
        <PatchDetailsModal
          show={showPatchModal}
          selectedRow={null}
          heartbeatResponse={null}
          isLoading={false}
          onClose={() => setShowPatchModal(false)}
        />
      )}
    </div>
  );
};

export default DeviceNeedingPatch;

// Strips a leading alphabetic prefix from a version string, e.g. AnyDesk
// versions stored as "ad1.19.0" become "1.19.0" for comparison/display.
function stripVersionPrefix(version?: string | null): string {
  if (!version) return '';
  return version.replace(/^[A-Za-z]+/, '');
}

function compareVersions(a: string, b: string): number {
  const pa = stripVersionPrefix(a).split('.').map(Number);
  const pb = stripVersionPrefix(b).split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}
